import { SignedIn, UserButton, useUser } from "@clerk/clerk-react";
import { Link, Outlet, useLocation } from "@tanstack/react-router";
import { Settings } from "lucide-react";
import { AccessGate } from "./components/AccessGate";
import { ConfettiProvider } from "./components/Confetti";
import { OfficeFilter } from "./components/filters/OfficeFilter";
import { HelpOverlay } from "./components/HelpOverlay";
import { Login } from "./components/Login";
import { RealtimeStatusBadge } from "./components/RealtimeStatusBadge";
import { ToastContainer, ToastProvider } from "./components/Toast";
import { TimeZoneDisplay } from "./components/TimeZoneDisplay";
import { CategoriesProvider } from "./contexts/CategoriesContext";
import { DealStagesProvider } from "./contexts/DealStagesContext";
import { LoggedInEmployeeProvider } from "./contexts/LoggedInEmployeeContext";
import { OfficesProvider } from "./contexts/OfficesContext";
import { RealtimeConnectionProvider } from "./contexts/RealtimeConnectionContext";
import { RecentlyChangedRowsProvider } from "./contexts/RecentlyChangedRowsContext";
import { RolesProvider } from "./contexts/RolesContext";
import { UserSettingsProvider } from "./contexts/UserSettingsContext";
import { useQueryParams } from "./hooks/useQueryParams";
import { useScrollbarWidthVar } from "./hooks/useScrollbarWidthVar";
import { UserTimeZoneProvider } from "./hooks/useUserTimeZone";
import "./index.css";
import { isE2EMode } from "./lib/e2e";

/**
 * Read the live query string as a plain object so it can be handed to
 * `<Link search={...}>`. Passing the current params keeps filters (e.g. the
 * selected office) intact when navigating between top-level pages, mirroring the
 * old behavior of appending `location.search` to every nav href.
 */
function useLiveSearch(): Record<string, string> {
  // Subscribe to query-param changes so the header re-renders (and rebuilds
  // these params) whenever a filter is toggled.
  useQueryParams();
  if (typeof window === "undefined") return {};
  return Object.fromEntries(new URLSearchParams(window.location.search));
}

function AppHeader() {
  const { pathname } = useLocation();
  const search = useLiveSearch();

  const isHome = pathname === "/";
  const isMakers = pathname === "/makers";
  const isActionItems = pathname === "/action-items";
  const isSettings = pathname === "/settings";

  // In E2E mode there is no Clerk session, so render the nav unconditionally
  // (Clerk's <SignedIn> would otherwise hide it). The UserButton stays gated
  // on a real session since it requires Clerk auth state.
  const e2e = isE2EMode();

  const settingsGear = (
    <Link
      to="/settings"
      search={search}
      className={`board-controls__icon-button app-header__gear ${isSettings ? "app-header__gear--active" : ""}`}
      aria-label="Settings"
      title="Settings"
    >
      <Settings size={20} />
    </Link>
  );

  const nav = (
    <nav className="app-header__nav">
      <Link
        to="/action-items"
        search={search}
        className={`app-header__nav-link ${isActionItems ? "app-header__nav-link--active" : ""}`}
      >
        To Do List
      </Link>
      <Link
        to="/"
        search={search}
        className={`app-header__nav-link ${isHome ? "app-header__nav-link--active" : ""}`}
      >
        Projects
      </Link>
      <Link
        to="/makers"
        search={search}
        className={`app-header__nav-link ${isMakers ? "app-header__nav-link--active" : ""}`}
      >
        Makers
      </Link>
      <div>
        <OfficeFilter />
      </div>
    </nav>
  );

  return (
    <header className="app-header">
      <div className="app-header__brand">
        <div className="brand-logo" aria-hidden>
          <img src="/public/capacitor-logo.svg" alt="Capacitor Logo" />
        </div>
        <span className="brand-title">Capacitor</span>
        {/* Realtime connection indicator sits with the logo on the left. */}
        <RealtimeStatusBadge />
      </div>
      {e2e ? (
        <>
          {nav}
          <div className="app-header__actions">
            <TimeZoneDisplay />
            <HelpOverlay />
            {settingsGear}
          </div>
        </>
      ) : (
        <SignedIn>
          {nav}
          <div className="app-header__actions">
            <TimeZoneDisplay />
            <HelpOverlay />
            {settingsGear}
            <UserButton />
          </div>
        </SignedIn>
      )}
    </header>
  );
}

/**
 * Root layout for the app. Rendered by the router's root route, it hosts the
 * shared provider tree, the header, and an <Outlet/> for the active page. Because
 * this component stays mounted across client-side navigations, switching pages no
 * longer tears down and rebuilds the providers — only the outlet content changes.
 */
export function App() {
  const { isSignedIn, isLoaded } = useUser();
  const { pathname } = useLocation();
  useScrollbarWidthVar();

  // The Projects page ("/", including its Current and Archived tabs) scrolls the
  // full page: the tabs scroll away while the sticky header stays and the
  // floating controls take over at the top. Other pages scroll within an inner
  // container and keep the header fixed.
  const isPageScroll = pathname === "/";

  return isE2EMode() || (isLoaded && isSignedIn) ? (
    <ToastProvider>
      <ConfettiProvider>
        <RealtimeConnectionProvider>
          <RecentlyChangedRowsProvider>
            <LoggedInEmployeeProvider>
              <AccessGate>
                <OfficesProvider>
                  <UserTimeZoneProvider>
                    <RolesProvider>
                      <DealStagesProvider>
                        <CategoriesProvider>
                          <UserSettingsProvider>
                            <div
                              className={`app-shell${isPageScroll ? " app-shell--page-scroll" : ""}`}
                            >
                              <AppHeader />
                              <main className="app-main">
                                <Outlet />
                              </main>
                            </div>
                          </UserSettingsProvider>
                          <ToastContainer />
                        </CategoriesProvider>
                      </DealStagesProvider>
                    </RolesProvider>
                  </UserTimeZoneProvider>
                </OfficesProvider>
              </AccessGate>
            </LoggedInEmployeeProvider>
          </RecentlyChangedRowsProvider>
        </RealtimeConnectionProvider>
      </ConfettiProvider>
    </ToastProvider>
  ) : (
    <Login />
  );
}

export default App;
